<template>
  <div class="two-factor-enrollment">
    <h6 class="sub-section-title">
      Two-Factor Authentication
    </h6>

    <div class="info-content">
      <div
        v-if="status.isLoading && !status.isVerifying"
        class="text-center"
      >
        <div class="info-comment">
          Loading...
        </div>
      </div>

      <template v-else>
        <div class="info-comment">
          Add an extra layer of security to your account by requiring a verification code in addition to your password.
          <br>
          [You can use any authenticator app like Google Authenticator or Microsoft Authenticator]
        </div>

        <div
          v-if="!isEnabled"
          class="qr-section mt-4"
        >
          <div class="info-comment">
            Scan this QR code with your authenticator app to set up two-factor authentication
          </div>
          <div class="qr-code-container">
            <img
              v-if="qrCode"
              :src="qrCode"
              alt="2FA QR Code"
            >
            <div
              v-else
              class="qr-placeholder"
            >
              Loading QR Code...
            </div>
          </div>
          
          <div class="setup-key-section mt-3">
            <a
              href="#"
              class="setup-key-link"
              @click.prevent="showSetupKey = !showSetupKey"
            >
              {{ showSetupKey ? 'Hide' : 'Show' }} Setup Key
            </a>
            <div
              v-if="showSetupKey && secret"
              class="setup-key mt-2"
            >
              <div class="info-comment">
                Enter this key manually in your authenticator app:
              </div>
              <div class="secret-key">
                {{ secret }}
              </div>
            </div>
          </div>

          <div class="verification-code mt-3">
            <input 
              v-model="verificationCode" 
              type="text" 
              class="form-control"
              placeholder="Enter OTP Code"
              :disabled="status.isVerifying"
              pattern="[0-9]*"
              maxlength="6"
              @keyup.enter="verifyCode"
            >
            <button 
              class="btn btn-primary mt-2" 
              :disabled="status.isVerifying || !verificationCode"
              @click="verifyCode"
            >
              {{ status.isVerifying ? 'Verifying...' : 'Verify' }}
            </button>
          </div>
        </div>

        <div
          v-else
          class="mt-4"
        >
          <div class="alert alert-success">
            Two-factor authentication is enabled for your account.
          </div>
        </div>

        <message-html :result-html="status.resultHtml" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserType } from '@/use/userType';

import { sendGet, sendPost } from '@/Utils/api';
import util from '@/Utils/util';

const router = useRouter();
const { userType } = useUserType();
const isEnabled = ref(false);
const showSetupKey = ref(false);
const verificationCode = ref('');
const qrCode = ref('');
const secret = ref('');

const status = reactive({
  isLoading: false,
  isVerifying: false,
  resultHtml: '',
});


const resetState = () => {
  showSetupKey.value = false;
  qrCode.value = '';
  secret.value = '';
  verificationCode.value = '';
};

const checkStatus = async () => {
  status.isLoading = true;
  try {
    const response = await sendGet('2fa/status');
    isEnabled.value = response.data.enabled;
    if (!isEnabled.value) {
      await setupTwoFactor();
    }
  } catch (error: any) {
    if (error.response?.status === 401) {
      router.push({
        path: `/${userType.value}/re-authenticate`,
        query: { returnUrl: `/${userType.value}/2fa` }
      });
      return;
    }
    status.resultHtml = util.getErrorResponse(error);
  } finally {
    status.isLoading = false;
  }
};

const setupTwoFactor = async () => {
  status.isLoading = true;
  try {
    const response = await sendPost('2fa/setup', {});
    qrCode.value = response.data.qrCode;
    secret.value = response.data.secret;
  } catch (error: any) {
    if (error.response?.status === 401) {
      router.push({
        path: `/${userType.value}/re-authenticate`,
        query: { returnUrl: `/${userType.value}/2fa` }
      });
      return;
    }
    status.resultHtml = util.getErrorResponse(error);
    resetState();
  } finally {
    status.isLoading = false;
  }
};

const verifyCode = async () => {
  if (!verificationCode.value) return;

  status.isVerifying = true;
  status.isLoading = true;
  try {
    await sendPost('2fa/verify-setup', { token: verificationCode.value });
    status.resultHtml = util.setSuccessMessage('Two-factor authentication has been enabled successfully.');
    isEnabled.value = true;
    verificationCode.value = '';
  } catch (error: any) {
    if (error.response?.status === 401) {
      router.push({
        path: `/${userType.value}/re-authenticate`,
        query: { returnUrl: `/${userType.value}/2fa` }
      });
      return;
    }
    status.resultHtml = util.getErrorResponse(error);
  } finally {
    status.isLoading = false;
    status.isVerifying = false;
  }
};

onMounted(checkStatus)
</script>

<style lang="scss">
.two-factor-enrollment {
  .info-content {
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    .info-comment {
      color: #666;
      font-size: 14px;
      line-height: 1.5;
    }

    .qr-section {
      text-align: center;
      
      .qr-code-container {
        margin: 20px auto;
        width: 200px;
        height: 200px;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        
        img {
          max-width: 100%;
          max-height: 100%;
        }
        
        .qr-placeholder {
          color: #666;
        }
      }

      .setup-key-section {
        .setup-key-link {
          color: #007bff;
          text-decoration: none;
          font-size: 14px;
          
          &:hover {
            text-decoration: underline;
          }
        }

        .setup-key {
          .secret-key {
            font-family: monospace;
            font-size: 16px;
            padding: 10px;
            background: #f8f9fa;
            border: 1px solid #ddd;
            border-radius: 4px;
            margin-top: 8px;
            user-select: all;
          }
        }
      }

      .verification-code {
        max-width: 300px;
        margin: 0 auto;
      }
    }

  }
}
</style> 